import firebase from 'firebase/app'
import 'firebase/database'
import axios from 'axios'
import format from 'date-fns/format'

const apiUrl = process.env.VUE_APP_API_URL

// Slack Integration Types
interface SlackChannel {
  id: string
  name: string
}

interface SlackConfiguration {
  workspaceId: string
  workspaceName: string
  botToken: string
  connected: boolean
  channels: {
    recognition: { channelId: string; channelName: string; enabled: boolean }
    teamChannels: boolean
    dmRecipient: boolean
  }
  features: {
    recognitionBroadcast: boolean
    reportSnippets: { daily: boolean; weekly: boolean }
    nudges: { enabled: boolean; triggerDays: number; maxPerWeek: number }
    reactionSync: boolean
  }
  installedAt?: string
  installedBy?: string
}

interface SlackState {
  configuration: SlackConfiguration | null
  loading: boolean
  error: string | null
  availableChannels: SlackChannel[]
  userMapping: Record<string, string>
  oauthUrl: string | null
}

const defaultConfiguration: SlackConfiguration = {
  workspaceId: '',
  workspaceName: '',
  botToken: '',
  connected: false,
  channels: {
    recognition: { channelId: '', channelName: '', enabled: true },
    teamChannels: false,
    dmRecipient: false
  },
  features: {
    recognitionBroadcast: true,
    reportSnippets: { daily: true, weekly: true },
    nudges: { enabled: true, triggerDays: 3, maxPerWeek: 2 },
    reactionSync: true
  }
}

// Helper function to safely get nested property
function getConfigValue(config: SlackConfiguration | null, path: string, defaultValue: any = null) {
  if (!config) return defaultValue
  const keys = path.split('.')
  let obj: any = config
  for (const key of keys) {
    if (obj == null || typeof obj !== 'object') return defaultValue
    obj = obj[key]
  }
  return obj != null ? obj : defaultValue
}

export default {
  namespaced: true,

  state: {
    configuration: null,
    loading: false,
    error: null,
    availableChannels: [],
    userMapping: {},
    oauthUrl: null
  },

  mutations: {
    setConfiguration(state: any, config: SlackConfiguration | null) {
      state.configuration = config
    },
    setLoading(state: any, loading: boolean) {
      state.loading = loading
    },
    setError(state: any, error: string | null) {
      state.error = error
    },
    setAvailableChannels(state: any, channels: SlackChannel[]) {
      state.availableChannels = channels
    },
    setUserMapping(state: any, mapping: Record<string, string>) {
      state.userMapping = mapping
    },
    setOAuthUrl(state: any, url: string | null) {
      state.oauthUrl = url
    },
    updateFeature(state: any, payload: { feature: string; value: any }) {
      if (state.configuration) {
        const keys = payload.feature.split('.')
        let obj: any = state.configuration.features
        for (let i = 0; i < keys.length - 1; i++) {
          obj = obj[keys[i]]
        }
        obj[keys[keys.length - 1]] = payload.value
      }
    },
    updateChannel(state: any, payload: { setting: string; value: any }) {
      if (state.configuration) {
        const keys = payload.setting.split('.')
        let obj: any = state.configuration.channels
        for (let i = 0; i < keys.length - 1; i++) {
          obj = obj[keys[i]]
        }
        obj[keys[keys.length - 1]] = payload.value
      }
    }
  },

  actions: {
    // Initialize OAuth flow - returns URL to redirect user to Slack
    async initiateOAuth({ commit, rootState }: any) {
      commit('setLoading', true)
      commit('setError', null)
      console.log('apiUrl', apiUrl);
      try {
        const companyId = rootState.authen.user.company
        const response = await axios.post(apiUrl + '/slack/oauth/initiate', {
          companyId: companyId,
          redirectUri: window.location.origin + '/integrations/slack/callback'
        })
        
        if (response.data.oauthUrl) {
          commit('setOAuthUrl', response.data.oauthUrl)
          return response.data.oauthUrl
        }
        throw new Error('Failed to get OAuth URL')
      } catch (error) {
        const err = error as any
        // Don't set error state for 404 - let component handle it gracefully
        var is404 = (err.response && err.response.status === 404) ||
                    (err.message && err.message.indexOf('404') !== -1)
        if (!is404) {
          commit('setError', err.message || 'Failed to initiate Slack OAuth')
        }
        throw error
      } finally {
        commit('setLoading', false)
      }
    },

    // Handle OAuth callback - exchange code for tokens
    async handleOAuthCallback({ commit, rootState, dispatch }: any, code: string) {
      commit('setLoading', true)
      commit('setError', null)

      try {
        const companyId = rootState.authen.user.company
        const userEmail = rootState.authen.user.email

        const response = await axios.post(apiUrl + '/slack/oauth/callback', {
          code: code,
          companyId: companyId,
          redirectUri: window.location.origin + '/integrations/slack/callback',
          installedBy: userEmail
        })

        if (response.data.success) {
          console.log('response.data', response);
          // Store configuration in Firebase
          // const config: SlackConfiguration = {
          //   workspaceId: response.data.workspaceId,
          //   workspaceName: response.data.workspaceName,
          //   botToken: response.data.botToken,
          //   connected: true,
          //   channels: defaultConfiguration.channels,
          //   features: defaultConfiguration.features,
          //   installedAt: format(new Date(), 'YYYY-MM-DD HH:mm:ss'),
          //   installedBy: userEmail
          // }

          // await firebase.database().ref('company/' + companyId).update({
          //   slack_integration: config,
          //   updated: format(new Date(), 'YYYY-MM-DD HH:mm:ss')
          // })

          const config = (await  firebase.database().ref('company/' + companyId+'/slack_integration').once('value')).val()

          commit('setConfiguration', config)

          // Fetch available channels
          await dispatch('fetchChannels')

          return true
        }
        throw new Error(response.data.error || 'OAuth callback failed')
      } catch (error) {
        const err = error as any
        commit('setError', err.message || 'Failed to complete Slack OAuth')
        throw error
      } finally {
        commit('setLoading', false)
      }
    },

    // Fetch current Slack configuration
    async fetchConfiguration({ commit, rootState }: any) {
      commit('setLoading', true)
      commit('setError', null)

      try {
        const companyId = rootState.authen.user.company
        const snapshot = await firebase.database().ref('company/' + companyId + '/slack_integration').once('value')
        const config = snapshot.val()

        if (config && config.connected) {
          commit('setConfiguration', config)
        } else {
          commit('setConfiguration', null)
        }

        return config
      } catch (error) {
        const err = error as any
        commit('setError', err.message || 'Failed to fetch Slack configuration')
        throw error
      } finally {
        commit('setLoading', false)
      }
    },

    // Update Slack configuration
    async updateConfiguration({ commit, rootState, state }: any, updates: Partial<SlackConfiguration>) {
      commit('setLoading', true)
      commit('setError', null)

      try {
        const companyId = rootState.authen.user.company
        const currentConfig = state.configuration || defaultConfiguration

        const newConfig = {
          workspaceId: updates.workspaceId || currentConfig.workspaceId,
          workspaceName: updates.workspaceName || currentConfig.workspaceName,
          botToken: updates.botToken || currentConfig.botToken || '',
          connected: updates.connected !== undefined ? updates.connected : currentConfig.connected,
          channels: Object.assign({}, currentConfig.channels, updates.channels || {}),
          features: Object.assign({}, currentConfig.features, updates.features || {}),
          installedAt: currentConfig.installedAt,
          installedBy: currentConfig.installedBy
        }

        await firebase.database().ref('company/' + companyId).update({
          slack_integration: newConfig,
          updated: format(new Date(), 'YYYY-MM-DD HH:mm:ss')
        })

        commit('setConfiguration', newConfig)
        return newConfig
      } catch (error) {
        const err = error as any
        commit('setError', err.message || 'Failed to update Slack configuration')
        throw error
      } finally {
        commit('setLoading', false)
      }
    },

    // Disconnect Slack integration
    async disconnect({ commit, rootState }: any) {
      commit('setLoading', true)
      commit('setError', null)

      try {
        const companyId = rootState.authen.user.company
        console.log('companyId', companyId);
        console.log('apiUrl', apiUrl);

        // Revoke tokens on backend
        await axios.post(apiUrl + '/slack/disconnect', { companyId: companyId })
        
        // // Clear configuration in Firebase
        // await firebase.database().ref('company/' + companyId + '/slack_integration').remove()

        commit('setConfiguration', null)
        commit('setAvailableChannels', [])
        commit('setUserMapping', {})

        return true
      } catch (error) {
        const err = error as any
        commit('setError', err.message || 'Failed to disconnect Slack')
        throw error
      } finally {
        commit('setLoading', false)
      }
    },

    // Fetch available Slack channels
    async fetchChannels({ commit, rootState, state }: any) {
      if (!state.configuration || !state.configuration.connected) return []

      try {
        const companyId = rootState.authen.user.company
        const response = await axios.get(apiUrl + '/slack/channels', {
          params: { companyId: companyId }
        })

        if (response.data.channels) {
          commit('setAvailableChannels', response.data.channels)
          return response.data.channels
        }
        return []
      } catch (error) {
        console.error('Failed to fetch Slack channels:', error)
        return []
      }
    },

    // // Map users by email to Slack user IDs
    // async syncUserMapping({ commit, rootState, state }: any) {
    //   if (!state.configuration || !state.configuration.connected) return {}

    //   try {
    //     const companyId = rootState.authen.user.company
    //     const response = await axios.post('http://localhost:80' + '/slack/users/sync', { companyId: companyId })

    //     if (response.data.mapping) {
    //       commit('setUserMapping', response.data.mapping)

    //       // Store mapping in Firebase for quick lookups
    //       await firebase.database().ref('company/' + companyId + '/slack_user_mapping').set(response.data.mapping)

    //       return response.data.mapping
    //     }
    //     return {}
    //   } catch (error) {
    //     console.error('Failed to sync user mapping:', error)
    //     return {}
    //   }
    // },

    // Post recognition to Slack (called after recognition is created)
    async broadcastRecognition({ rootState, state }: any, recognition: any) {
      if (!state.configuration || !state.configuration.connected) {
        return null
      }
      if (!state.configuration.features || !state.configuration.features.recognitionBroadcast) {
        return null
      }

      try {
        const companyId = rootState.authen.user.company
        const response = await axios.post(apiUrl + '/slack/recognition/broadcast', {
          companyId: companyId,
          recognition: recognition,
          // channelConfig: state.configuration.channels
        })

        return response.data
      } catch (error) {
        console.error('Failed to broadcast recognition to Slack:', error)
        return null
      }
    },

    // Send daily report snippet to manager via Slack DM
    async sendDailyReportSnippet({ rootState, state }: any, report: any) {
      if (!state.configuration || !state.configuration.connected) {
        return null
      }
      var reportSnippets = state.configuration.features && state.configuration.features.reportSnippets
      if (!reportSnippets || !reportSnippets.daily) {
        return null
      }

      try {
        var companyId = rootState.authen.user.company
        var response = await axios.post(apiUrl + '/slack/report/daily', {
          companyId: companyId,
          managerEmail: report.managerEmail,
          teamName: report.teamName,
          date: report.date,
          narrative: report.narrative,
          metrics: report.metrics
        })
        return response.data
      } catch (error) {
        console.error('Failed to send daily report snippet to Slack:', error)
        return null
      }
    },

    // Send weekly report snippet to manager via Slack DM
    async sendWeeklyReportSnippet({ rootState, state }: any, report: any) {
      if (!state.configuration || !state.configuration.connected) {
        return null
      }
      var reportSnippets = state.configuration.features && state.configuration.features.reportSnippets
      if (!reportSnippets || !reportSnippets.weekly) {
        return null
      }

      try {
        var companyId = rootState.authen.user.company
        var response = await axios.post(apiUrl + '/slack/report/weekly', {
          companyId: companyId,
          managerEmail: report.managerEmail,
          teamName: report.teamName,
          weekStart: report.weekStart,
          weekEnd: report.weekEnd,
          narrative: report.narrative,
          metrics: report.metrics
        })
        return response.data
      } catch (error) {
        console.error('Failed to send weekly report snippet to Slack:', error)
        return null
      }
    },

    // Test Slack connection
    async testConnection({ rootState, state }: any) {
      if (!state.configuration || !state.configuration.connected) {
        return { success: false, error: 'Not connected' }
      }

      try {
        const companyId = rootState.authen.user.company
        const response = await axios.post(apiUrl + '/slack/test', { companyId: companyId })
        return response.data
      } catch (error) {
        const err = error as any
        return { success: false, error: err.message }
      }
    }
  },

  getters: {
    isConnected: function(state: any) {
      return state.configuration && state.configuration.connected ? true : false
    },
    workspaceName: function(state: any) {
      return state.configuration && state.configuration.workspaceName ? state.configuration.workspaceName : ''
    },
    configuration: function(state: any) {
      return state.configuration
    },
    loading: function(state: any) {
      return state.loading
    },
    error: function(state: any) {
      return state.error
    },
    availableChannels: function(state: any) {
      return state.availableChannels
    },

    // Feature flags
    recognitionBroadcastEnabled: function(state: any) {
      return state.configuration && state.configuration.features && state.configuration.features.recognitionBroadcast ? true : false
    },
    dailyReportSnippetsEnabled: function(state: any) {
      return state.configuration && state.configuration.features && state.configuration.features.reportSnippets && state.configuration.features.reportSnippets.daily ? true : false
    },
    weeklyReportSnippetsEnabled: function(state: any) {
      return state.configuration && state.configuration.features && state.configuration.features.reportSnippets && state.configuration.features.reportSnippets.weekly ? true : false
    },
    nudgesEnabled: function(state: any) {
      return state.configuration && state.configuration.features && state.configuration.features.nudges && state.configuration.features.nudges.enabled ? true : false
    },
    reactionSyncEnabled: function(state: any) {
      return state.configuration && state.configuration.features && state.configuration.features.reactionSync ? true : false
    },

    // Channel settings
    recognitionChannel: function(state: any) {
      if (state.configuration && state.configuration.channels && state.configuration.channels.recognition) {
        return state.configuration.channels.recognition
      }
      return { channelId: '', channelName: '', enabled: false }
    },
    teamChannelsEnabled: function(state: any) {
      return state.configuration && state.configuration.channels && state.configuration.channels.teamChannels ? true : false
    },
    dmRecipientEnabled: function(state: any) {
      return state.configuration && state.configuration.channels && state.configuration.channels.dmRecipient ? true : false
    },

    // Nudge settings
    nudgeTriggerDays: function(state: any) {
      if (state.configuration && state.configuration.features && state.configuration.features.nudges && state.configuration.features.nudges.triggerDays) {
        return state.configuration.features.nudges.triggerDays
      }
      return 3
    },
    nudgeMaxPerWeek: function(state: any) {
      if (state.configuration && state.configuration.features && state.configuration.features.nudges && state.configuration.features.nudges.maxPerWeek) {
        return state.configuration.features.nudges.maxPerWeek
      }
      return 2
    }
  }
}
